import React from 'react';
import PropTypes from 'prop-types';

import {
  Body,
  Container,
  Grid,
  Select,
  H6,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@platform/ui';
import module, { math } from '../../../modules/dicom-measurement/src';

const { round } = math;
const { guideline, getTimepoints } = module;
const {
  BODY_PARTS,
  BRAIN_LOCATION,
  getMeasurementLesionTypes,
  getBodyParts,
  getTrackingTypes,
} = guideline;

export const MeasurementSection = ({
  imageSets,
  ROIContours,
  criteria,
  onROIUpdate,
  getFilteredROIContours,
  filteredBodyPart,
  setFilteredBodyPart,
  filteredTrackingType,
  setFilteredTrackingType,
  selectedLesionNumber,
  setSelectedLesionNumber,
  timepointIndex,
  setTimepointIndex,
}) => {
  const timepoints = getTimepoints(imageSets, ROIContours);
  const filteredROIContours = getFilteredROIContours(ROIContours);

  const onBodyPartChange = evt => {
    setFilteredBodyPart(evt.target.value);
  };
  const onTrackingTypeChange = evt => {
    const type = evt.target.value;
    setFilteredTrackingType(type);
    if (type !== 'selected') {
      setSelectedLesionNumber(-1);
    }
  };
  const onLesionNumberChange = evt => {
    setSelectedLesionNumber(Number(evt.target.value));
  };
  const onTimepointChange = evt => {
    setTimepointIndex(evt.target.value);
  };

  const VolumeMM = (
    <span key={'volume'}>
      Volume(mm<sup>3</sup>)
    </span>
  );

  return (
    <Body>
      <Container>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow index={-1}>
              {[
                ['No.', '4%'],
                ['Name', '12%'],
                ['Location', '12%'],
                ['Long Axis(mm)', '12%'],
                ['Short Axis(mm)', '12%'],
                [VolumeMM, '12%'],
                ['Measurability', '12%'],
                ['Body Part', '12%'],
                ['Type', '12%'],
              ].map(([text, width]) => (
                <TableCell key={text} width={width} header={1}>
                  <H6>{text}</H6>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredROIContours.map((roi, index) => {
              const timepoint = timepoints[timepointIndex];
              const lesion = timepoint.Lesions.find(
                d => d.ROINumber === roi.ROINumber
              );
              const longAxis = round(lesion['maxLongAxisValue'], -2);
              const shortAxis = round(lesion['maxShortAxisValue'], -2);
              const volume = round(lesion['volume'], -2);
              return (
                <Row
                  key={roi.ROINumber}
                  index={index}
                  criteria={criteria}
                  roi={roi}
                  onROIUpdate={onROIUpdate}
                  longAxis={longAxis}
                  shortAxis={shortAxis}
                  volume={volume}
                />
              );
            })}
          </TableBody>
        </Table>
      </Container>
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <Select value={filteredBodyPart} onChange={onBodyPartChange}>
            {getBodyParts().map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Grid>
        <Grid item xs={3}>
          <Select value={filteredTrackingType} onChange={onTrackingTypeChange}>
            {getTrackingTypes(criteria.guidelineId).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Grid>
        <Grid item xs={3}>
          <Select value={selectedLesionNumber} onChange={onLesionNumberChange}>
            <option key={-1} value={-1}>
              ------
            </option>
            {filteredTrackingType === 'selected' &&
              ROIContours.map(roi => (
                <option key={roi.ROINumber} value={roi.ROINumber}>
                  {roi.ROIName}
                </option>
              ))}
          </Select>
        </Grid>
        <Grid item xs={3}>
          <Select value={timepointIndex} onChange={onTimepointChange}>
            {timepoints.map((timepoint, index) => (
              <option key={timepoint.SeriesDate + index} value={index}>
                {timepoint.SeriesDate}
              </option>
            ))}
          </Select>
        </Grid>
      </Grid>
    </Body>
  );
};
MeasurementSection.propTypes = {
  imageSets: PropTypes.array,
  ROIContours: PropTypes.array,
  criteria: PropTypes.shape({
    guidelineId: PropTypes.string,
  }),
  onROIUpdate: PropTypes.func,
  getFilteredROIContours: PropTypes.func,
  filteredBodyPart: PropTypes.string,
  setFilteredBodyPart: PropTypes.func,
  filteredTrackingType: PropTypes.string,
  setFilteredTrackingType: PropTypes.func,
  selectedLesionNumber: PropTypes.number,
  setSelectedLesionNumber: PropTypes.func,
  timepointIndex: PropTypes.number,
  setTimepointIndex: PropTypes.func,
};

const Row = ({
  index,
  criteria,
  roi,
  onROIUpdate,
  longAxis,
  shortAxis,
  volume,
}) => {
  const { ROIName, measurable, bodyPart, trackingType } = roi;
  const key = Object.keys(BRAIN_LOCATION).find(l => ROIName.includes(l));
  const location = BRAIN_LOCATION[key] || 'N/A';
  const measurability = measurable ? 'measurable' : 'non-measurable';
  const onBodyPartChange = evt => {
    onROIUpdate(roi.ROINumber, evt.target.value, trackingType);
  };
  const onTrackingTypeChange = evt => {
    onROIUpdate(roi.ROINumber, bodyPart, evt.target.value);
  };

  return (
    <TableRow index={index}>
      {[
        index,
        ROIName,
        location,
        longAxis,
        shortAxis,
        volume,
        measurability,
      ].map((text, i) => (
        <TableCell key={i}>
          <H6>{text}</H6>
        </TableCell>
      ))}
      <TableCell>
        <Select value={bodyPart} onChange={onBodyPartChange}>
          {[
            [BODY_PARTS.BRAIN, 'Brain'],
            [BODY_PARTS.NECK, 'Neck'],
            [BODY_PARTS.LIVER, 'Liver'],
            [BODY_PARTS.LUNG, 'Lung'],
            [BODY_PARTS.LYMPHNODE, 'Lymph Node'],
            [BODY_PARTS.PLEURA, 'Pleura'],
            [BODY_PARTS.PLEURALEFFUSION, 'Pleural Effusion'],
            [BODY_PARTS.ADRENALGLAND, 'Adrenal Gland'],
            [BODY_PARTS.BONE, 'Bone'],
            [BODY_PARTS.OTHERS, 'Others'],
          ].map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
      </TableCell>
      <TableCell>
        <Select value={trackingType} onChange={onTrackingTypeChange}>
          {getMeasurementLesionTypes(criteria.guidelineId).map(i => (
            <option key={i.id} value={i.id}>
              {i.label}
            </option>
          ))}
        </Select>
      </TableCell>
    </TableRow>
  );
};
Row.propTypes = {
  index: PropTypes.number,
  criteria: PropTypes.shape({
    guidelineId: PropTypes.string,
  }),
  roi: PropTypes.shape({
    ROIName: PropTypes.string,
    ROINumber: PropTypes.number,
    measurable: PropTypes.bool,
    bodyPart: PropTypes.string,
    trackingType: PropTypes.string,
  }),
  onROIUpdate: PropTypes.func,
  longAxis: PropTypes.number,
  shortAxis: PropTypes.number,
  volume: PropTypes.number,
};
